import type { ClearanceVertex } from './types';
import { rectangleProfile } from './profile';

/**
 * The clearance profiles we ship, as typed vertex generators (spec §1:
 * "Shipped presets live client-side as vertex generators, not DB rows").
 *
 * Every dimension here is a PUBLISHED FACT copied from
 * `docs/superpowers/research/2026-08-31-clearance-profiles.md`, which cites
 * the source document for each one. Dimensions are facts and are not
 * copyrightable; the drawings that carry them are, so we reproduce numbers and
 * never diagrams. Each preset carries a `sourceAttribution` string that the
 * picker prints verbatim and that is snapshotted onto every run (ruling K4),
 * always ending in "verify against the current standard" — these standards are
 * revised, and a survey signed off against a stale envelope is the failure
 * mode this line exists to prevent.
 *
 * NOT shipped, deliberately: anything derived from AREMA Chapter 28 (paywalled
 * and derivative-prohibited), AAR MSRP-C plates, UIC 505-x, EN 15273/50119 or
 * RSSB documents. No UIC/Berne gauge either — it is a vehicle gauge, not a
 * structure gauge, so sweeping it would answer a different question.
 */

export type ClearancePresetId = 'roadBox' | 'tcE05Diagram1' | 'tcE05Diagram3' | 'ca8ccr' | 'artcAbcd' | 'artcF';

export interface ClearancePreset {
  id: ClearancePresetId;
  /** `parametric` presets take user dimensions; `standard` ones are fixed. */
  kind: 'parametric' | 'standard';
  /** i18n key under `clearance.presets`, so the picker is translated ×5. */
  nameKey: string;
  /** Printed in the picker and stored on the run's snapshot (K4). ≤ 300 chars. */
  sourceAttribution: string;
  /**
   * Fixed standard outlines are READ-ONLY in the editor (spec §1). Editing one
   * in place would leave a polygon still labelled "TC E-05 Diagram 1" that no
   * longer is one — "Save a copy" detaches it first.
   */
  readOnly: boolean;
  vertices: ClearanceVertex[];
}

// ---------------------------------------------------------------------------
// Road clearance box — parametric (research report §2.1–§2.3, §4.1)
// ---------------------------------------------------------------------------

/**
 * The named vertical clearances. The WIDTH is deliberately not among them:
 * DMRB CD 127 §4.1/§4.6 applies headroom over the Structure Free Zone, whose
 * width is a site-measured quantity, so the user enters the half-width.
 *
 * `verticalOffsetM` doubles as CD 127's sag-curve compensation S and as the
 * US future-overlay allowance, so a UK user checking a sag underbridge types
 * S = 0.045 and it means exactly what the standard means.
 */
export const ROAD_BOX_HEIGHTS = [
  {
    id: 'ukMaintained',
    metres: 5.03,
    sourceAttribution:
      'Height per DMRB CD 127 Table 4.1, maintained overbridge (5.03 m + S) — verify against the current standard',
  },
  {
    id: 'ukNew',
    metres: 5.3,
    sourceAttribution:
      'Height per DMRB CD 127 Table 4.1, new overbridge (5.30 m + S) — verify against the current standard',
  },
  {
    id: 'ukHighLoad',
    metres: 6.18,
    sourceAttribution:
      'Height per DMRB CD 127 Table 4.1, maintained structure over a high load route (6.18 m + S) — verify against the current standard',
  },
  {
    id: 'usInterstateRural',
    metres: 4.9,
    sourceAttribution:
      'Height per FHWA/AASHTO Interstate design standards (23 CFR 625), rural Interstate 4.9 m (16 ft) — verify against the current standard',
  },
  {
    id: 'usUrbanSecondary',
    metres: 4.3,
    sourceAttribution:
      'Height per FHWA/AASHTO Interstate design standards (23 CFR 625), non-single-routing urban Interstate 4.3 m (14 ft) — verify against the current standard',
  },
  {
    id: 'euAgr',
    metres: 4.5,
    sourceAttribution:
      'Height per UNECE AGR (Geneva 1975) Ch. III.4, minimum overhead clearance 4.5 m — verify against the current standard',
  },
] as const satisfies readonly { id: string; metres: number; sourceAttribution: string }[];

/**
 * How far above the carriageway the road box STARTS.
 *
 * The box used to begin at 0 — flush with the road surface — which makes the
 * running surface itself an intrusion: the pavement is inside the envelope,
 * every point on it is a violation, and its "intrusion depth" is just how high
 * it sits. On a real 885 m run that produced 8.9 M violations of which the
 * worst point in 29 of 44 station bins was under 0.5 m above the datum: the
 * road, reported as an obstruction of the road.
 *
 * 0.15 m is where that stops. Below it a base barely helps (at +0.05 m, 29 of
 * those 44 bins are still flagged; at +0.10 m, 27), and above it nothing more
 * is gained (+0.30 m and +0.50 m both leave the same 15 as +0.20 m) — the
 * remainder are real objects. It also clears what a road surface legitimately
 * varies by: cross-fall over a 3-4 m half-width is ~0.10 m, and the alignment's
 * own vertical error rides on top of that.
 *
 * Unlike the rail outlines' 0, this bottom edge is NOT a published dimension.
 * A road headroom standard fixes the height OVER the carriageway and says
 * nothing about a floor, so the floor is ours to choose — which is exactly why
 * it may be changed here and the rail diagrams' may not.
 */
export const ROAD_BOX_DEFAULT_BASE_M = 0.15;

/**
 * `baseM` lifts the FLOOR only; the published headroom stays measured from the
 * carriageway. A 5.03 m maintained overbridge with a 0.15 m base is a box from
 * 0.15 m to 5.03 m — not 0.15 m to 5.18 m. Raising the roof with the floor
 * would quietly restate the standard's own figure, which is the one number
 * here that is not ours to move.
 */
export const roadBoxPreset = (
  halfWidthM: number,
  heightId: string,
  baseM: number = ROAD_BOX_DEFAULT_BASE_M,
): ClearancePreset => {
  const height = ROAD_BOX_HEIGHTS.find((h) => h.id === heightId);
  // Throw rather than fall back: silently substituting a different legal
  // headroom is precisely the wrong-but-plausible answer this tool exists to
  // catch, and it would be invisible on the sketch.
  if (!height) throw new Error(`Unknown road clearance height "${heightId}"`);
  // A base at or above the headroom would invert the box into a zero/negative
  // height polygon, which `validateProfileVertices` then rejects as
  // degenerate — a confusing way to learn you typed 6 into a 5.03 m box.
  const base = Number.isFinite(baseM) ? Math.min(Math.max(0, baseM), height.metres - MIN_ROAD_BOX_HEIGHT_M) : 0;
  return {
    id: 'roadBox',
    kind: 'parametric',
    nameKey: 'roadBox',
    sourceAttribution: height.sourceAttribution,
    readOnly: false,
    vertices: rectangleProfile({ widthM: halfWidthM * 2, heightM: height.metres - base, baseM: base }),
  };
};

/** Keeps a clamped base from collapsing the box to nothing. */
const MIN_ROAD_BOX_HEIGHT_M = 0.5;

// ---------------------------------------------------------------------------
// Fixed standard outlines
// ---------------------------------------------------------------------------

/**
 * Transport Canada TC E-05 Diagram 1 — structures over or beside the track.
 * Datum: top of rail, centreline of the outside track. Three symmetric bands
 * (half-width by height above rail): 1168 mm to 127 mm, 1753 mm to 1219 mm,
 * 2546 mm to 6706 mm.
 *
 * TC E-05 §3(e) adds a lateral allowance of 25.4 mm per degree of curve. That
 * is CURVE WIDENING, deferred in v1 (spec §4) — it is a lateral offset, not a
 * cant rotation — so this outline is the tangent-track case.
 */
const TC_E05_DIAGRAM_1: ClearanceVertex[] = [
  [-1.168, 0],
  [1.168, 0],
  [1.168, 0.127],
  [1.753, 0.127],
  [1.753, 1.219],
  [2.546, 1.219],
  [2.546, 6.706],
  [-2.546, 6.706],
  [-2.546, 1.219],
  [-1.753, 1.219],
  [-1.753, 0.127],
  [-1.168, 0.127],
];

/**
 * TC E-05 Diagram 3 — railway tunnels. Vertical walls at ±2439 mm with a
 * semicircular crown of R = 2439 mm centred on the centreline 4267 mm above
 * top of rail, giving a 6706 mm crown; same 127 mm / 1168 mm base band as
 * Diagram 1. The arc is discretised with 8 segments — well inside the
 * 64-vertex cap and visually smooth in the sketch at any zoom the dialog uses.
 */
const TC_E05_DIAGRAM_3: ClearanceVertex[] = [
  [-1.168, 0],
  [1.168, 0],
  [1.168, 0.127],
  [2.439, 0.127],
  [2.439, 4.267],
  [2.253, 5.2],
  [1.725, 5.992],
  [0.933, 6.52],
  [0, 6.706],
  [-0.933, 6.52],
  [-1.725, 5.992],
  [-2.253, 5.2],
  [-2.439, 4.267],
  [-2.439, 0.127],
  [-1.168, 0.127],
];

/**
 * California 8 CCR §3736–3738 — the freely-citable North American structure
 * envelope. §3736: 22'-6" (6.858 m) over top of rail. §3737: 8'-6" (2.591 m)
 * from the centreline of tangent track. §3738 rounds the corners with a
 * semicircle of radius = the side clearance, tangent to the overhead line,
 * putting its centre at 14'-0" (4.267 m) — the same construction TC E-05
 * Diagram 3 uses, which is a strong mutual confirmation of both readings.
 *
 * No 127 mm base band: that detail is Canadian, not Californian. §3737 also
 * adds 1'-0" on curves — curve widening again, deferred.
 */
const CA_8CCR: ClearanceVertex[] = [
  [-2.591, 0],
  [2.591, 0],
  [2.591, 4.267],
  [2.394, 5.259],
  [1.832, 6.099],
  [0.992, 6.661],
  [0, 6.858],
  [-0.992, 6.661],
  [-1.832, 6.099],
  [-2.394, 5.259],
  [-2.591, 4.267],
];

/**
 * ARTC ETS-07-00 Appendix B3, Structure Outline ABCD (tangent track, W = 0).
 * Heights above design rail level 3700 / 4350 / 4800 mm, half-widths
 * 2200 + W / 1810 + W / 800 + W, closed flat at rail level.
 *
 * ARTC §7.2.1.6 is explicit that the STRUCTURE outline is measured to the
 * plane of gravity and is NOT rotated with the track — which is why spec §4
 * defers cant rotation as the correct model rather than a shortcut. What a
 * curve does add is the tabulated asymmetric W1/W2 widening, also deferred.
 */
const ARTC_ABCD: ClearanceVertex[] = [
  [-2.2, 0],
  [2.2, 0],
  [2.2, 3.7],
  [1.81, 4.35],
  [0.8, 4.8],
  [-0.8, 4.8],
  [-1.81, 4.35],
  [-2.2, 3.7],
];

/** ARTC ETS-07-00 Appendix B6, Structure Outline F (double-stack, tangent). */
const ARTC_F: ClearanceVertex[] = [
  [-2.5, 0],
  [2.5, 0],
  [2.5, 6.8],
  [1.3, 7.1],
  [-1.3, 7.1],
  [-2.5, 6.8],
];

export const STANDARD_PRESETS: readonly ClearancePreset[] = [
  {
    id: 'tcE05Diagram1',
    kind: 'standard',
    nameKey: 'tcE05Diagram1',
    sourceAttribution:
      'Dimensions per Transport Canada E-05, Diagram 1 (tangent track; curve widening not applied) — verify against the current standard',
    readOnly: true,
    vertices: TC_E05_DIAGRAM_1,
  },
  {
    id: 'tcE05Diagram3',
    kind: 'standard',
    nameKey: 'tcE05Diagram3',
    sourceAttribution:
      'Dimensions per Transport Canada E-05, Diagram 3, tunnels (tangent track; curve widening not applied) — verify against the current standard',
    readOnly: true,
    vertices: TC_E05_DIAGRAM_3,
  },
  {
    id: 'ca8ccr',
    kind: 'standard',
    nameKey: 'ca8ccr',
    sourceAttribution:
      'Dimensions per California 8 CCR §3736–3738 (tangent track; the curve allowance is not applied) — verify against the current standard',
    readOnly: true,
    vertices: CA_8CCR,
  },
  {
    id: 'artcAbcd',
    kind: 'standard',
    nameKey: 'artcAbcd',
    // Finding F-10: unlike every other preset here, ARTC ETS-07-00 itself
    // restricts reliance ("may not be relied on by any other party without
    // ARTC's prior written consent" — research §4.4) — the generic "verify
    // against the current standard" sentence the other presets use is not
    // sufficient for this one, and the standard's name has to survive into
    // the final clause too.
    sourceAttribution:
      "Dimensions per ARTC ETS-07-00 Structure Outline ABCD, tangent track (W = 0; curve widening not applied). May not be relied on by any other party without ARTC's prior written consent — verify against the current ARTC standard",
    readOnly: true,
    vertices: ARTC_ABCD,
  },
  {
    id: 'artcF',
    kind: 'standard',
    nameKey: 'artcF',
    // Finding F-10 — see the ABCD preset above for why this diverges from
    // the generic attribution sentence.
    sourceAttribution:
      "Dimensions per ARTC ETS-07-00 Structure Outline F, tangent track (W = 0; curve widening not applied). May not be relied on by any other party without ARTC's prior written consent — verify against the current ARTC standard",
    readOnly: true,
    vertices: ARTC_F,
  },
];

export const getPreset = (id: ClearancePresetId): ClearancePreset | undefined =>
  STANDARD_PRESETS.find((p) => p.id === id);
